import { FC } from 'react';
import { Modal } from '@leagueplatform/genesis-core';
import { useProductSegmentation } from 'hooks/use-product-segmentation.hook';
import { CommunicationOption } from 'models/profile-settings.model';
import { PaperlessConfirmationModal } from 'components/modals/paperless-confirmation-modal.component';
import { UnsubscribeWarningModal } from 'components/modals/unsubscribe-warning-modal.component';
import { useCommunicationPreference } from 'hooks/communications/use-communication-preference.hook';
import {
  sendCommunicationButtonClickedEvent,
  getCommunicationDetailFromId,
  getCommunicationContentNameFromId,
  sendAccountSetupCommunicationButtonClickedEvent,
} from 'utils/analytics-events';
import { CommunicationEditForm } from './communication-edit-form.component';
import { DetailSection } from '../detail-section.component';

interface CommunicationComponentProps {
  title: string;
  initialOptions: CommunicationOption[];
  alwaysEdit?: boolean;
  showBorder?: boolean;
  hideButtons?: boolean;
  disableUnsavedModal?: boolean;
  noPadding?: boolean;
  isAccountSetup?: boolean;

  onInputChange?: (id: string, value: boolean) => void;
  onSave?: (options: CommunicationOption[]) => void;
}

export const CommunicationComponent: FC<CommunicationComponentProps> = ({
  title,
  initialOptions,
  alwaysEdit = false,
  showBorder = true,
  hideButtons = false,
  disableUnsavedModal = false,
  noPadding,
  onInputChange,
  onSave,
  isAccountSetup,
}) => {
  const { showPaperlessSetup } = useProductSegmentation();
  const {
    contents,
    handleSave: handleLocalSave,
    handleInputChange: internalHandleInputChange,
    showPaperlessModal,
    setShowPaperlessModal,
    showUnsubscribeModal,
    setShowUnsubscribeModal,
    handlePaperlessConfirm,
    handlePaperlessCancel,
    handleUnsubscribeConfirm,
    handleUnsubscribeCancel,
    currentOptions,
  } = useCommunicationPreference(initialOptions, isAccountSetup);

  const handleSave = () => {
    sendCommunicationButtonClickedEvent('save');
    onSave?.(currentOptions);
  };

  const handleReset = () => {
    handleLocalSave(
      Object.fromEntries(currentOptions.map((opt) => [opt.labelId, opt.value])),
    );
  };

  const renderEditContent = ({
    onChange,
  }: {
    editValues: Record<string, string>;
    onChange: (id: string, value: boolean) => void;
    isMobile: boolean;
  }) => {
    const editValues = Object.fromEntries(
      currentOptions.map((opt) => [opt.labelId, String(opt.value)]),
    ) as Record<string, string>;

    return (
      <CommunicationEditForm
        showPaperlessSetup={showPaperlessSetup}
        editValues={editValues}
        onInputChange={(id: string, value: string | boolean) => {
          const booleanValue = value === 'true' || value === true;
          if (isAccountSetup) {
            const contentName = getCommunicationContentNameFromId(
              id,
              booleanValue,
            );
            sendAccountSetupCommunicationButtonClickedEvent(
              contentName,
              booleanValue,
            );
          } else {
            const detail = getCommunicationDetailFromId(id, booleanValue);
            sendCommunicationButtonClickedEvent(detail, booleanValue);
          }
          internalHandleInputChange(id, booleanValue);
          onInputChange?.(id, booleanValue);
          onChange(id, booleanValue);
        }}
      />
    );
  };

  return (
    <DetailSection
      title={title}
      contents={contents}
      onSave={handleSave}
      renderEditContent={(props: any) => renderEditContent(props)}
      alwaysEdit={alwaysEdit}
      showBorder={showBorder}
      noPadding={noPadding}
      hideButtons={hideButtons}
      disableUnsavedModal={disableUnsavedModal}
      reset={handleReset}
      modals={
        showPaperlessSetup ? (
          <>
            <Modal.Root
              open={showPaperlessModal}
              onOpenChange={() => setShowPaperlessModal(false)}
            >
              <PaperlessConfirmationModal
                onConfirm={handlePaperlessConfirm}
                onClose={handlePaperlessCancel}
              />
            </Modal.Root>
            <Modal.Root
              open={showUnsubscribeModal}
              onOpenChange={() => setShowUnsubscribeModal(false)}
            >
              <UnsubscribeWarningModal
                onConfirm={handleUnsubscribeConfirm}
                onClose={handleUnsubscribeCancel}
              />
            </Modal.Root>
          </>
        ) : null
      }
    />
  );
};

export default CommunicationComponent;
